import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { GoogleLogin } from '@react-oauth/google';
import { Compass, List, Mail, MessageCircle, UsersRound } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { authApi } from '@watchparty/shared';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { TmdbAttribution } from '@/components/TmdbAttribution';
import watchpartyIcon from '@/assets/icons/watchparty-icon.png';
const features = [
    { icon: UsersRound, title: 'Private parties', description: "Create a Watch Party and invite the people you actually want to watch with" },
    { icon: List, title: 'Shared watch list', description: "One collaborative list per party — add what's next, see what everyone's thinking" },
    { icon: MessageCircle, title: 'In-the-moment chat', description: "Message your party while you watch — reactions, hot takes, and spoilers welcome" },
    { icon: Compass, title: 'Discover & browse', description: "Explore a catalog built from millions of titles, from blockbusters to deep cuts" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function getErrorMessage(error) {
    if (error instanceof Error)
        return error.message;
    return 'Something went wrong.';
}
function AppleLogo() {
    return (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 17 20", fill: "currentColor", children: _jsx("path", { d: "M13.545 10.239c-.022-2.234 1.823-3.306 1.906-3.357-.037-.054-1.495-2.22-3.476-2.22-1.48 0-2.676.879-3.387.879-.749 0-1.86-.857-3.06-.834C3.63 4.732 1.886 5.93 1.886 8.57c0 1.581.614 3.254 1.37 4.338.753 1.084 1.471 2.053 2.518 2.015 1.012-.04 1.393-.654 2.614-.654 1.197 0 1.544.654 2.604.634 1.087-.017 1.706-.935 2.446-2.025.771-1.163 1.089-2.289 1.107-2.348-.024-.01-2.125-.817-2.146-3.236l.146-.055zM11.433 3.114c.594-.762 1.012-1.79.9-2.839-1.065.052-2.41.752-3.128 1.528-.618.672-1.181 1.782-1.036 2.826 1.192.088 2.375-.595 3.264-1.515z" }) }));
}
export function LandingPage() {
    const navigate = useNavigate();
    const auth = useAuth();
    const [activeTab, setActiveTab] = useState('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [isAppleLoading, setIsAppleLoading] = useState(false);
    const [oauthError, setOauthError] = useState(null);
    const [verificationEmail, setVerificationEmail] = useState(null);
    const [resendingEmail, setResendingEmail] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    // Google button width tracking
    const [googleContainerRef, setGoogleContainerRef] = useState(null);
    const [googleWidth, setGoogleWidth] = useState(360);
    useEffect(() => {
        if (!googleContainerRef)
            return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setGoogleWidth(Math.min(360, Math.max(200, Math.floor(entry.contentRect.width))));
            }
        });
        observer.observe(googleContainerRef);
        return () => observer.disconnect();
    }, [googleContainerRef]);
    function switchTab(tab) {
        setActiveTab(tab);
        setError(null);
        setOauthError(null);
    }
    function validate() {
        if (!EMAIL_RE.test(email))
            return 'Please enter a valid email address.';
        if (password.length < 8)
            return 'Password must be at least 8 characters.';
        if (activeTab === 'create') {
            if (!displayName.trim())
                return 'Display name is required.';
            if (displayName.trim().length > 64)
                return 'Display name must be 64 characters or fewer.';
            if (password !== confirmPassword)
                return 'Passwords do not match.';
        }
        return null;
    }
    async function handleSubmit(e) {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setIsSubmitting(true);
        try {
            if (activeTab === 'signin') {
                await auth.login({ email, password });
            }
            else {
                const response = await auth.register({ email, password, displayName: displayName.trim() });
                if (response.emailVerificationRequired) {
                    setVerificationEmail(email);
                    return;
                }
            }
            const pendingToken = sessionStorage.getItem('pendingJoinToken');
            if (pendingToken) {
                sessionStorage.removeItem('pendingJoinToken');
                await navigate({ to: '/join/$token', params: { token: pendingToken } });
            }
            else {
                await navigate({ to: '/parties' });
            }
        }
        catch (err) {
            setError(getErrorMessage(err));
        }
        finally {
            setIsSubmitting(false);
        }
    }
    async function handleGoogleSuccess(response) {
        if (!response.credential) {
            setOauthError('Google sign-in failed. No credential received.');
            return;
        }
        setOauthError(null);
        setIsGoogleLoading(true);
        try {
            await authApi.googleSignIn(response.credential);
            await auth.refreshUser();
            const pendingToken = sessionStorage.getItem('pendingJoinToken');
            if (pendingToken) {
                sessionStorage.removeItem('pendingJoinToken');
                await navigate({ to: '/join/$token', params: { token: pendingToken } });
            }
            else {
                await navigate({ to: '/parties' });
            }
        }
        catch (err) {
            setOauthError(getErrorMessage(err));
        }
        finally {
            setIsGoogleLoading(false);
        }
    }
    async function handleAppleSignIn() {
        setOauthError(null);
        setIsAppleLoading(true);
        try {
            AppleID.auth.init({
                clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
                scope: 'email name',
                redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI,
                usePopup: true,
            });
            const response = await AppleID.auth.signIn();
            const identityToken = response.authorization.id_token;
            const fullName = response.user
                ? `${response.user.name.firstName} ${response.user.name.lastName}`.trim()
                : null;
            await authApi.appleSignIn(identityToken, fullName);
            await auth.refreshUser();
            const pendingToken = sessionStorage.getItem('pendingJoinToken');
            if (pendingToken) {
                sessionStorage.removeItem('pendingJoinToken');
                await navigate({ to: '/join/$token', params: { token: pendingToken } });
            }
            else {
                await navigate({ to: '/parties' });
            }
        }
        catch (err) {
            if (err && typeof err === 'object' && 'error' in err) {
                const appleErr = err;
                if (appleErr.error === 'popup_closed_by_user')
                    return;
            }
            setOauthError(getErrorMessage(err));
        }
        finally {
            setIsAppleLoading(false);
        }
    }
    return (_jsxs("div", { className: "landing-layout", style: {
            display: 'flex',
            minHeight: '100vh',
            background: 'var(--color-bg)',
        }, children: [_jsxs("div", { className: "landing-left", style: {
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '24px',
                    background: 'var(--color-surface)',
                    overflow: 'hidden',
                }, children: [_jsx("div", { style: {
                            pointerEvents: 'none',
                            position: 'absolute', inset: 0,
                            background: 'radial-gradient(ellipse at 30% 20%, rgba(201,168,76,0.03) 0%, transparent 70%)',
                        } }), _jsx("div", { style: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }, children: _jsxs("div", { style: { position: 'relative', zIndex: 1, maxWidth: '512px', margin: '0 auto' }, children: [_jsxs("p", { style: { fontFamily: 'var(--font-family-heading)', fontSize: '2.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '14px' }, children: [_jsx("img", { src: watchpartyIcon, alt: "", "aria-hidden": "true", style: { width: '80px', height: '80px' } }), _jsxs("span", { children: [_jsx("span", { style: { color: 'var(--color-text)' }, children: "Watch" }), _jsx("span", { style: { color: 'var(--color-accent)' }, children: "Party" })] })] }), _jsxs("h1", { style: {
                                        marginTop: '12px',
                                        fontFamily: 'var(--font-family-heading)',
                                        fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                                        fontWeight: 700,
                                        lineHeight: 1.2,
                                        letterSpacing: '-0.02em',
                                        color: 'var(--color-text)',
                                    }, children: ["Watch whenever.", _jsx("br", {}), "Track together."] }), _jsx("p", { style: {
                                        marginTop: '8px',
                                        fontSize: '1rem',
                                        lineHeight: 1.6,
                                        color: 'var(--color-text-muted)',
                                        fontFamily: 'var(--font-family-body)',
                                    }, children: "Create private parties with friends. Build a watch list together. Chat about what you're watching as it happens." }), _jsx("div", { className: "landing-features", children: features.map((feature) => (_jsxs("div", { style: {
                                            display: 'flex', alignItems: 'flex-start', gap: '16px',
                                            borderLeft: '2px solid rgba(201, 168, 76, 0.25)',
                                            paddingLeft: '14px',
                                        }, children: [_jsx("div", { style: {
                                                    width: '40px', height: '40px', flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    borderRadius: 'var(--radius-lg)',
                                                    background: 'var(--color-surface-sunken)',
                                                }, children: _jsx(feature.icon, { size: 20, style: { color: 'var(--color-accent)' } }) }), _jsxs("div", { children: [_jsx("p", { style: { fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-accent)', fontFamily: 'var(--font-family-body)' }, children: feature.title }), _jsx("p", { style: { marginTop: '2px', fontSize: '0.875rem', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-body)' }, children: feature.description })] })] }, feature.title))) })] }) }), _jsxs("div", { className: "landing-copyright", style: {
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            zIndex: 1,
                            paddingTop: '16px',
                        }, children: [_jsxs("div", { style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    flexWrap: 'wrap',
                                }, children: [_jsxs("span", { style: { fontSize: '0.6875rem', color: 'var(--color-text-faint)', fontFamily: 'var(--font-family-body)' }, children: ["\u00A9 ", new Date().getFullYear(), " ", _jsx("span", { style: { color: 'var(--color-text-faint)' }, children: "Watch" }), _jsx("span", { style: { color: 'var(--color-accent)', opacity: 0.5 }, children: "Party" })] }), _jsx("span", { style: { fontSize: '0.6875rem', color: 'var(--color-text-faint)' }, children: "\u00B7" }), [
                                        { to: '/about/data', label: 'About Our Data' },
                                        { to: '/support', label: 'Support' },
                                        { to: '/legal/privacy', label: 'Privacy' },
                                        { to: '/legal/terms', label: 'Terms' },
                                    ].map((link, i, arr) => (_jsxs("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '6px' }, children: [_jsx(Link, { to: link.to, className: "landing-footer-link", style: {
                                                    fontSize: '0.6875rem',
                                                    color: 'var(--color-text-faint)',
                                                    fontFamily: 'var(--font-family-body)',
                                                    textDecoration: 'none',
                                                }, children: link.label }), i < arr.length - 1 && (_jsx("span", { style: { fontSize: '0.6875rem', color: 'var(--color-text-faint)' }, children: "\u00B7" }))] }, link.to)))] }), _jsx(TmdbAttribution, {})] })] }), _jsx("div", { className: "landing-right", style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    background: 'var(--color-bg)',
                }, children: verificationEmail ? (_jsxs("div", { style: { width: '100%', maxWidth: '360px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }, children: [_jsx("div", { style: {
                                width: '56px', height: '56px', borderRadius: 'var(--radius-full)',
                                background: 'var(--color-surface-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }, children: _jsx(Mail, { size: 28, style: { color: 'var(--color-accent)' } }) }), _jsx("h2", { style: { fontFamily: 'var(--font-family-heading)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)' }, children: "Check your email" }), _jsxs("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.9375rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }, children: ["We sent a verification link to ", _jsx("strong", { style: { color: 'var(--color-text)' }, children: verificationEmail })] }), _jsx("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.8125rem', color: 'var(--color-text-faint)', lineHeight: 1.5 }, children: "Click the link in the email to verify your account, then come back here to sign in." }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', width: '100%' }, children: [_jsx(Button, { type: "button", variant: "default", disabled: resendingEmail, onClick: async () => {
                                        setResendingEmail(true);
                                        setResendSuccess(false);
                                        try {
                                            await authApi.resendVerification(verificationEmail);
                                            setResendSuccess(true);
                                        }
                                        catch {
                                            // silently fail — don't leak info
                                        }
                                        finally {
                                            setResendingEmail(false);
                                        }
                                    }, style: {
                                        width: '100%',
                                        background: 'var(--color-surface-raised)',
                                        color: 'var(--color-text)',
                                    }, children: resendingEmail ? 'Sending...' : 'Resend email' }), resendSuccess && (_jsx("p", { style: { fontSize: '0.8125rem', color: 'var(--color-success, #4caf50)', fontFamily: 'var(--font-family-body)' }, children: "Verification email sent!" })), _jsx("button", { type: "button", onClick: () => {
                                        setVerificationEmail(null);
                                        setResendSuccess(false);
                                    }, style: {
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        fontFamily: 'var(--font-family-body)', fontSize: '0.8125rem',
                                        color: 'var(--color-accent)', textDecoration: 'underline',
                                        padding: '4px',
                                    }, children: "Use a different email" })] })] })) : (_jsxs("div", { style: { width: '100%', maxWidth: '360px' }, children: [_jsx("div", { style: { display: 'flex', borderBottom: '1px solid var(--color-border)' }, children: ['signin', 'create'].map((tab) => {
                                const isActive = activeTab === tab;
                                const label = tab === 'signin' ? 'Sign In' : 'Create Account';
                                return (_jsx("button", { type: "button", onClick: () => switchTab(tab), style: {
                                        flex: 1, paddingBottom: '12px', textAlign: 'center',
                                        fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', fontWeight: 700,
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                                        color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
                                        marginBottom: '-1px',
                                        transition: 'color 200ms, background-color 200ms',
                                    }, children: label }, tab));
                            }) }), _jsxs("form", { onSubmit: handleSubmit, style: { marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }, children: [activeTab === 'create' && (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsx(Label, { htmlFor: "displayName", children: "Display Name" }), _jsx(Input, { id: "displayName", type: "text", placeholder: "Your name", value: displayName, onChange: (e) => setDisplayName(e.target.value), maxLength: 64, autoComplete: "name" })] })), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsx(Label, { htmlFor: "email", children: "Email" }), _jsx(Input, { id: "email", type: "email", placeholder: "you@example.com", value: email, onChange: (e) => setEmail(e.target.value), autoComplete: "email" })] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsx(Label, { htmlFor: "password", children: "Password" }), _jsx(Input, { id: "password", type: "password", placeholder: "Min. 8 characters", value: password, onChange: (e) => setPassword(e.target.value), autoComplete: activeTab === 'create' ? 'new-password' : 'current-password' })] }), activeTab === 'create' && (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' }, children: [_jsx(Label, { htmlFor: "confirmPassword", children: "Confirm Password" }), _jsx(Input, { id: "confirmPassword", type: "password", placeholder: "Re-enter password", value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value), autoComplete: "new-password" })] })), error && (_jsx("p", { style: { fontSize: '0.875rem', color: 'var(--color-danger)', fontFamily: 'var(--font-family-body)' }, children: error })), _jsx(Button, { type: "submit", variant: "default", disabled: isSubmitting, style: {
                                        width: '100%',
                                        background: 'var(--color-accent)',
                                        color: 'var(--color-text-inverse)',
                                        transition: 'color 200ms, background-color 200ms, box-shadow 200ms',
                                    }, onMouseEnter: e => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                                        e.currentTarget.style.boxShadow = '0 0 12px var(--color-accent-glow)';
                                    }, onMouseLeave: e => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                                        e.currentTarget.style.boxShadow = '';
                                    }, children: isSubmitting
                                        ? (activeTab === 'signin' ? 'Signing in...' : 'Creating account...')
                                        : (activeTab === 'signin' ? 'Sign In' : 'Create Account') })] }), activeTab === 'signin' && (_jsx("div", { style: { textAlign: 'center', marginTop: '8px' }, children: _jsx(Link, { to: "/forgot-password", style: {
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.8125rem',
                                    color: 'var(--color-text-muted)',
                                    textDecoration: 'none',
                                }, children: "Forgot your password?" }) })), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }, children: [_jsx("div", { style: { flex: 1, height: '1px', background: 'var(--color-border)' } }), _jsx("span", { style: { fontSize: '0.75rem', color: 'var(--color-text-faint)', fontFamily: 'var(--font-family-body)' }, children: "or" }), _jsx("div", { style: { flex: 1, height: '1px', background: 'var(--color-border)' } })] }), _jsx("div", { ref: setGoogleContainerRef, children: isGoogleLoading ? (_jsx("button", { disabled: true, style: {
                                    display: 'flex', width: '100%', height: '40px',
                                    alignItems: 'center', justifyContent: 'center',
                                    borderRadius: 'var(--radius-md)',
                                    background: 'var(--color-surface-sunken)',
                                    border: '1px solid var(--color-border)',
                                    fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', fontWeight: 500,
                                    color: 'var(--color-text-muted)',
                                    cursor: 'not-allowed',
                                }, children: "Signing in..." })) : (_jsx("div", { style: { display: 'flex', justifyContent: 'center' }, children: _jsx(GoogleLogin, { onSuccess: handleGoogleSuccess, onError: () => setOauthError('Google sign-in was cancelled or failed.'), size: "large", width: String(googleWidth), text: "signin_with", shape: "rectangular", theme: "filled_blue" }) })) }), _jsx("div", { className: "apple-btn-wrapper", style: { marginTop: '8px' }, children: _jsx("button", { type: "button", onClick: handleAppleSignIn, disabled: isAppleLoading, style: {
                                    display: 'flex', width: '100%', height: '40px',
                                    alignItems: 'center', justifyContent: 'center', gap: '12px',
                                    borderRadius: 'var(--radius-md)',
                                    background: isAppleLoading ? 'var(--color-surface-sunken)' : '#000',
                                    border: '1px solid var(--color-border)',
                                    fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', fontWeight: 500,
                                    color: isAppleLoading ? 'var(--color-text-muted)' : '#fff',
                                    cursor: isAppleLoading ? 'not-allowed' : 'pointer',
                                }, children: isAppleLoading ? ('Signing in...') : (_jsxs(_Fragment, { children: [_jsx(AppleLogo, {}), "Sign in with Apple"] })) }) }), oauthError && (_jsx("p", { style: { marginTop: '8px', fontSize: '0.875rem', color: 'var(--color-danger)', fontFamily: 'var(--font-family-body)' }, children: oauthError }))] })) })] }));
}
